/**
 * Enhanced YouTube Embed Component - Plays YouTube videos with error handling and retry logic
 */
import React, { useState, useEffect, useRef } from 'react';
import { songsAPI, favoritesAPI } from '../services/api';
import { useAudio } from '../contexts/AudioContext';
import './YouTubeEmbed.css';

const YouTubeEmbed = ({ song, onClose, autoplay = true, onDownloadAttempt, allSongs = [], currentIndex = 0, onSongUpdated = null }) => {
  const { playSong } = useAudio();
  const [videoId, setVideoId] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [embedFailed, setEmbedFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isAttemptingDownload, setIsAttemptingDownload] = useState(false);
  const [isFavoriting, setIsFavoriting] = useState(false);
  const [isExtractingMetadata, setIsExtractingMetadata] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  // Load YouTube IFrame API
  useEffect(() => {
    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // Set up callback when API is ready
      window.onYouTubeIframeAPIReady = () => {
        initializePlayer();
      };
    } else {
      // API already loaded, initialize player directly
      initializePlayer();
    }

    return () => {
      // Clean up player when component unmounts
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [videoId]);

  // Initialize YouTube player
  const initializePlayer = () => {
    if (!videoId || !iframeRef.current) return;

    // Remove existing player if any
    if (playerRef.current) {
      playerRef.current.destroy();
    }

    // Create new player
    playerRef.current = new window.YT.Player(iframeRef.current, {
      videoId: videoId,
      playerVars: {
        autoplay: autoplay ? 1 : 0,
        rel: 0,
        modestbranding: 1,
        enablejsapi: 1
      },
      events: {
        onReady: (event) => {
          console.log('YouTube player ready');
          setEmbedFailed(false);
          setError(null);

          // Autoplay when ready if enabled
          if (autoplay) {
            try {
              event.target.playVideo();
              console.log('Autoplay started');
            } catch (error) {
              console.warn('Autoplay failed, user may need to interact first:', error);
            }
          }

          // Extract metadata after player loads
          extractAndStoreMetadata();
        },
        onStateChange: (event) => {
          // YT.PlayerState.ENDED = 0
          if (event.data === 0) {
            console.log('YouTube video ended, advancing to next song');
            handleNavigate('next');
          }
        },
        onError: (event) => {
          console.error('YouTube player error:', event.data);
          setEmbedFailed(true);
          let errorMsg = 'This video is unavailable or geo-blocked';

          // Provide more specific error messages based on error code
          switch (event.data) {
            case 2: // Invalid parameter
              errorMsg = 'Invalid YouTube video ID';
              break;
            case 5: // HTML5 player error
              errorMsg = 'YouTube player error - try refreshing';
              break;
            case 100: // Video not found
              errorMsg = 'This video has been removed or is not available';
              break;
            case 101: // Embed not allowed
            case 150: // Embed not allowed
              errorMsg = 'This video cannot be played on this site';
              break;
            default:
              errorMsg = 'This video is unavailable or geo-blocked';
          }

          setError(errorMsg);

          // Auto-advance to next song after 2 seconds if error persists
          setTimeout(() => {
            if (embedFailed) {
              console.log('Auto-skipping unavailable video after error');
              handleNavigate('next');
            }
          }, 2000);
        }
      }
    });
  };

  // Update URL with current video ID when song changes
  useEffect(() => {
    // Extract video ID from YouTube URL
    const extractVideoId = (url) => {
      if (!url) return null;

      // Handle various YouTube URL formats
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /^youtube\s+([a-zA-Z0-9_-]+)$/, // Handle "youtube video_id" format
        /^([a-zA-Z0-9_-]{11})$/ // Handle plain video ID
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          return match[1];
        }
      }

      // If it's already just a video ID
      if (url.length === 11 && /^[a-zA-Z0-9_-]+$/.test(url)) {
        return url;
      }

      return null;
    };

    const id = extractVideoId(song.youtube_url);
    if (id) {
      setVideoId(id);
      setError(null);
      setIsLoading(false);

      // Update URL with current video ID
      const url = new URL(window.location);
      url.searchParams.set('video', id);
      url.searchParams.set('songId', song.id);
      window.history.pushState({ songId: song.id, videoId: id }, '', url);
    } else {
      setError('Could not extract YouTube video ID');
      setIsLoading(false);
    }

    // Cleanup URL parameter when component unmounts
    return () => {
      const url = new URL(window.location);
      url.searchParams.delete('video');
      url.searchParams.delete('songId');
      window.history.pushState(null, '', url);
    };
  }, [song.youtube_url, song.id]);

  // Retry embed playback
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setEmbedFailed(false);
    setError(null);
    // Force iframe reload by changing key
    if (iframeRef.current) {
      iframeRef.current.src = `${iframeRef.current.src}&retry=${retryCount + 1}`;
    }
  };

  // Attempt to download the song
  const handleAttemptDownload = async () => {
    if (isAttemptingDownload) return;

    try {
      setIsAttemptingDownload(true);
      await songsAPI.downloadSong(song.id);
      alert('Download initiated! The song will be processed in the background.');
      onClose();
    } catch (error) {
      console.error('Failed to initiate download:', error);
      alert('Failed to start download. Please try again later.');
    } finally {
      setIsAttemptingDownload(false);
    }
  };

  // Toggle favorite
  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    if (isFavoriting) return;

    try {
      setIsFavoriting(true);
      if (song.is_favorite) {
        await favoritesAPI.removeFavorite(song.id);
        song.is_favorite = false;
      } else {
        await favoritesAPI.addFavorite(song.id);
        song.is_favorite = true;
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      alert('Failed to update favorite status');
    } finally {
      setIsFavoriting(false);
    }
  };

  // Extract and store metadata from YouTube
  const extractAndStoreMetadata = async () => {
    // Helper function to check if metadata is old (more than 7 days)
    const isMetadataOld = (lastUpdate) => {
      if (!lastUpdate) return true;
      const daysSinceUpdate = (Date.now() - new Date(lastUpdate).getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceUpdate > 7;
    };

    // Helper function to check if metadata has critical missing fields
    const hasIncompleteMetadata = (song) => {
      return !song.title ||
             song.title === 'Unknown Title' ||
             song.title === 'Unknown' ||
             !song.artist ||
             song.artist === 'Unknown Artist' ||
             !song.view_count ||
             !song.upload_date;
    };

    // Smart extraction logic: always extract for embed playback, but be smart about updates
    const shouldExtractMetadata =
      !song.last_metadata_update || // Never extracted
      isMetadataOld(song.last_metadata_update) || // Old data (more than 7 days)
      hasIncompleteMetadata(song); // Missing critical fields

    if (!shouldExtractMetadata) {
      console.log('Song metadata is recent and complete, skipping extraction');
      return;
    }

    if (isExtractingMetadata) {
      console.log('Metadata extraction already in progress');
      return;
    }

    try {
      setIsExtractingMetadata(true);
      console.log(`Extracting metadata for song ${song.id}: ${song.youtube_url}`);

      const response = await songsAPI.extractMetadata(song.id);

      if (response.data && response.data.song) {
        const updatedSong = response.data.song;
        console.log('Metadata extracted successfully:', updatedSong.title);

        // Update local song object with new metadata
        Object.assign(song, updatedSong);

        // Notify parent component if callback provided
        if (onSongUpdated) {
          onSongUpdated(updatedSong);
        }
      }
    } catch (error) {
      console.error('Failed to extract metadata:', error);
      // Don't show error to user - metadata extraction is silent/background
    } finally {
      setIsExtractingMetadata(false);
    }
  };

  // Handle navigation to previous/next song
  const handleNavigate = (direction) => {
    if (allSongs.length === 0) return;

    // Find current song index
    const currentIdx = allSongs.findIndex(s => s.id === song.id);
    if (currentIdx === -1) return;

    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIdx + 1) % allSongs.length;
    } else {
      nextIndex = currentIdx === 0 ? allSongs.length - 1 : currentIdx - 1;
    }

    const nextSong = allSongs[nextIndex];
    if (!nextSong) return;

    // Close current embed and play the next song
    onClose();

    // Use setTimeout to allow the current embed to close first
    setTimeout(() => {
      if (nextSong.download_status === 'completed') {
        // For completed songs, use the regular audio player
        playSong(nextSong, allSongs, nextIndex);
      } else {
        // For non-completed songs, trigger a new YouTube embed
        // This will be handled by the parent component
        const event = new CustomEvent('playYouTubeEmbed', {
          detail: { song: nextSong, index: nextIndex }
        });
        window.dispatchEvent(event);
      }
    }, 100);
  };

  if (error && embedFailed) {
    return (
      <div className="youtube-embed-overlay" onClick={onClose}>
        <div className="youtube-embed-container error-state" onClick={(e) => e.stopPropagation()}>
          <div className="youtube-embed-header">
            <h3>⚠️ Video Unavailable</h3>
            <button onClick={onClose} className="btn-close" title="Close player">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="youtube-embed-error-content">
            <div className="error-icon">⚠️</div>
            <h4>Unable to play this video</h4>
            <p className="error-message">{error}</p>
            <p className="error-details">
              Auto-advancing to next song in 2 seconds...
            </p>

            <div className="error-actions">
              <button
                onClick={() => {
                  // Cancel auto-advance and stay on this song
                  setEmbedFailed(false);
                }}
                className="btn-cancel"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
                Stay on This Song
              </button>

              <button
                onClick={() => handleNavigate('next')}
                className="btn-next"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                </svg>
                Skip to Next Song
              </button>

              <button
                onClick={handleAttemptDownload}
                className="btn-download"
                disabled={isAttemptingDownload}
              >
                {isAttemptingDownload ? (
                  <>
                    <div className="spinner-small" />
                    Starting Download...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                    </svg>
                    Try Downloading
                  </>
                )}
              </button>
            </div>

            <div className="error-info">
              <p><strong>Song:</strong> {song.title}</p>
              <p><strong>Artist:</strong> {song.artist || 'Unknown Artist'}</p>
              <a href={song.youtube_url} target="_blank" rel="noopener noreferrer" className="youtube-link">
                Open on YouTube →
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="youtube-embed-overlay" onClick={onClose}>
        <div className="youtube-embed-container error-state" onClick={(e) => e.stopPropagation()}>
          <div className="youtube-embed-header">
            <h3>⚠️ Error</h3>
            <button onClick={onClose} className="btn-close" title="Close player">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="youtube-embed-error-content">
            <div className="error-icon">⚠️</div>
            <h4>Unable to play video</h4>
            <p className="error-message">{error}</p>
            <button onClick={onClose} className="btn-close">Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !videoId) {
    return (
      <div className="youtube-embed-overlay" onClick={onClose}>
        <div className="youtube-embed-container" onClick={(e) => e.stopPropagation()}>
          <div className="youtube-embed-header">
            <h3>Loading...</h3>
            <button onClick={onClose} className="btn-close" title="Close player">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="youtube-embed-loading">
            <div className="spinner" />
            <p>Loading YouTube player...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="youtube-embed-overlay" onClick={onClose}>
      <div className="youtube-embed-container enhanced-player" onClick={(e) => e.stopPropagation()}>
        <div className="youtube-embed-header">
          <div className="header-info">
            <div className="playback-mode-badge youtube">
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
              </svg>
              YouTube Embed
            </div>
            <h3>Now Playing: {song.title}</h3>
          </div>
          <button onClick={onClose} className="btn-close" title="Close player">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="youtube-embed-player">
          <div
            ref={iframeRef}
            id={`youtube-player-${videoId}`}
            className="youtube-player-container"
          />
        </div>

        {/* Navigation Controls */}
        <div className="youtube-embed-nav">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate('previous');
            }}
            className="nav-btn prev-btn"
            title="Previous song"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
            Previous
          </button>

          <button
            onClick={handleToggleFavorite}
            className="nav-btn favorite-btn"
            title={song.is_favorite ? "Remove from favorites" : "Add to favorites"}
            disabled={isFavoriting}
          >
            {isFavoriting ? (
              <div className="spinner-small" />
            ) : (
              <svg viewBox="0 0 24 24" fill={song.is_favorite ? "currentColor" : "none"} stroke="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            )}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate('next');
            }}
            className="nav-btn next-btn"
            title="Next song"
          >
            Next
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
          </button>
        </div>

        <div className="youtube-embed-info">
          <div className="info-main">
            <p className="embed-artist">{song.artist || 'Unknown Artist'}</p>
            <div className="embed-status">
              <span className="status-badge youtube-stream">🎵 Streaming via YouTube</span>
              <span className="embed-hint">High quality audio • No download required</span>
            </div>
          </div>

          <div className="embed-actions">
            <a
              href={song.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-youtube"
              title="Open on YouTube"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
              </svg>
              Open on YouTube
            </a>

            {song.download_status !== 'completed' && (
              <button
                onClick={handleAttemptDownload}
                className="btn-download-embed"
                disabled={isAttemptingDownload}
                title="Download for offline playback"
              >
                {isAttemptingDownload ? (
                  <>
                    <div className="spinner-small" />
                    Starting...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                    </svg>
                    Download
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubeEmbed;